// src/components/Certificate.tsx

import React, {useState, useEffect} from 'react';
import axios from 'axios';
import {enqueueSnackbar} from 'notistack';
import {KeenIcon} from '@/components';
import {formatDateOnly} from '@/pages/buddy/utils/Date.tsx';
import {Link} from 'react-router-dom';

interface CertificationItem {
    name: string;
    issuing_organization?: string | null;
    date_issued?: string | null;
    credential_id?: string | null;
    credential_url?: string | null;
}

interface CertificateProps {
    title: string;
    items: CertificationItem[];
    className?: string;
    candidate_p_id: string;
}

/** Utility function to deep clone certification items */
function deepCloneCertificationItem(item: CertificationItem): CertificationItem {
    return {...item};
}

function deepCloneItems(items: CertificationItem[]): CertificationItem[] {
    return items.map(deepCloneCertificationItem);
}

const Certifications = ({title, items, candidate_p_id}: CertificateProps) => {
    const API_URL = import.meta.env.VITE_APP_API_URL;

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [originalItems, setOriginalItems] = useState<CertificationItem[]>([]);
    const [editedItems, setEditedItems] = useState<CertificationItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        setOriginalItems(deepCloneItems(items));
        setEditedItems(deepCloneItems(items));
    }, [items]);

    const handleEditClick = () => {
        setEditedItems(deepCloneItems(originalItems));
        setIsEditing(true);
    };

    const handleCancelClick = () => {
        setEditedItems(deepCloneItems(originalItems));
        setIsEditing(false);
    };

    const handleSaveClick = async () => {
        setLoading(true);

        for (const item of editedItems) {
            if (!item.name || !item.name.trim()) {
                enqueueSnackbar("Each certification must have a name.", {variant: 'warning'});
                setLoading(false);
                return;
            }
        }

        try {
            const token = localStorage.getItem('authToken') || '';
            const dataToSend = editedItems.map(item => ({
                name: item.name,
                issuing_organization: item.issuing_organization || null,
                date_issued: item.date_issued ? new Date(item.date_issued).toISOString() : null,
                credential_id: item.credential_id || null,
                credential_url: item.credential_url || null,
            }));

            await axios.put(
                `${API_URL}/candidate/${candidate_p_id}/certifications`,
                {certifications: dataToSend},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            setOriginalItems(deepCloneItems(editedItems));
            setIsEditing(false);
            enqueueSnackbar("Certifications updated successfully.", {variant: 'success'});
        } catch (err: any) {
            enqueueSnackbar(`Failed to update certifications: ${err.response?.data?.message || err.message}`, {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const createNewCertification = (): CertificationItem => ({
        name: '',
        issuing_organization: '',
        date_issued: '',
        credential_id: '',
        credential_url: '',
    });

    const handleAddCertification = (atTop: boolean = false) => {
        setEditedItems(prev => {
            const newItems = deepCloneItems(prev);
            if (atTop) {
                newItems.unshift(createNewCertification());
            } else {
                newItems.push(createNewCertification());
            }
            return newItems;
        });
    };

    const handleDeleteCertification = (index: number) => {
        setEditedItems(prev => {
            const newItems = deepCloneItems(prev);
            newItems.splice(index, 1);
            return newItems;
        });
    };

    type EditableStringFields = 'name' | 'issuing_organization' | 'date_issued' | 'credential_id' | 'credential_url';

    const handleEditField = (index: number, field: EditableStringFields) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setEditedItems(prev => {
                const newItems = deepCloneItems(prev);
                newItems[index][field] = value;
                return newItems;
            });
        };

    const renderItem = (item: CertificationItem, index: number) => {
        return (
            <div key={index} className="flex align-start gap-3.5">
                <KeenIcon icon="award" className="text-sm text-primary"/>

                <div className="flex flex-col gap-1 w-full">
                    {isEditing ? (
                        <>
                            <input
                                type="text"
                                className="input input-bordered input-sm w-full"
                                placeholder="Certification Name"
                                value={item.name}
                                onChange={handleEditField(index, 'name')}
                            />
                            <input
                                type="text"
                                className="input input-bordered input-sm w-full"
                                placeholder="Issuing Organization"
                                value={item.issuing_organization || ''}
                                onChange={handleEditField(index, 'issuing_organization')}
                            />
                            <input
                                type="date"
                                className="input input-bordered input-sm w-full"
                                placeholder="Date Issued"
                                value={item.date_issued ? item.date_issued.slice(0, 10) : ''}
                                onChange={handleEditField(index, 'date_issued')}
                            />
                            <input
                                type="text"
                                className="input input-bordered input-sm w-full"
                                placeholder="Credential ID"
                                value={item.credential_id || ''}
                                onChange={handleEditField(index, 'credential_id')}
                            />
                            <input
                                type="url"
                                className="input input-bordered input-sm w-full"
                                placeholder="Credential URL"
                                value={item.credential_url || ''}
                                onChange={handleEditField(index, 'credential_url')}
                            />
                            <div className="mt-2 flex justify-end">
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm text-red-500"
                                    onClick={() => handleDeleteCertification(index)}
                                    aria-label="Delete Certification"
                                >
                                    <KeenIcon icon="trash"/> Delete Certification
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <h4 className="text-md font-semibold">{item.name}</h4>

                            {(item.issuing_organization || item.credential_id) && (
                                <span className="text-xs text-gray-700">
                                    {item.issuing_organization}
                                    {item.issuing_organization && item.credential_id ? ' - ' : ''}
                                    {item.credential_id ? `ID: ${item.credential_id}` : ''}
                                </span>
                            )}

                            {item.date_issued && (
                                <span className="text-sm text-gray-700">
                                    {formatDateOnly(item.date_issued)}
                                </span>
                            )}

                            {item.credential_url && (
                                <a
                                    href={item.credential_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary hover:underline"
                                >
                                    View Credential
                                </a>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };

    // Function to sort certifications by date_issued in descending order
    const sortByDateDesc = (a: CertificationItem, b: CertificationItem) => {
        const dateA = a.date_issued ? new Date(a.date_issued).getTime() : 0;
        const dateB = b.date_issued ? new Date(b.date_issued).getTime() : 0;
        return dateB - dateA;
    };

    return (
        <div className="card">
            <div className="card-header flex justify-between items-center">
                <h3 className="card-title">{title}</h3>
                {!isEditing ? (
                    <button className="btn btn-light btn-sm flex items-center gap-1" onClick={handleEditClick}>
                        <KeenIcon icon="notepad-edit"/>
                        Edit
                    </button>
                ) : (
                    <div className="flex space-x-2">
                        <button
                            className="btn btn-primary btn-sm flex items-center gap-1"
                            onClick={handleSaveClick}
                            disabled={loading}
                        >
                            <KeenIcon icon="file-up"/>
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                            className="btn btn-secondary btn-sm flex items-center gap-1"
                            onClick={handleCancelClick}
                            disabled={loading}
                        >
                            <KeenIcon icon="tablet-delete"/>
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            <div className="card-body">
                {!isEditing ? (
                    <div className="grid gap-y-5">
                        {originalItems.length > 0 ? (
                            originalItems
                                .slice() // Create a shallow copy for sorting
                                .sort(sortByDateDesc)
                                .map((item, index) => renderItem(item, index))
                        ) : (
                            <span className="text-sm text-gray-500">No Certifications available.</span>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="grid gap-y-5">
                            {editedItems.length > 0 ? (
                                editedItems.map((item, index) => renderItem(item, index))
                            ) : (
                                <span className="text-sm text-gray-500">No Certifications to edit.</span>
                            )}
                        </div>
                        <div className="flex space-x-2 mt-4">
                            <button
                                type="button"
                                className="btn btn-success btn-sm flex items-center gap-1"
                                onClick={() => handleAddCertification()}
                            >
                                <KeenIcon icon="add-notepad"/>
                                Add Certification
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export {Certifications, type CertificationItem};
